export enum CountryCode {
  US = 'US',
  CA = 'CA',
  MX = 'MX',
}

// All valid ISO-3166-1 alpha-2 country codes (including US, territories, and foreign countries)
export const VALID_COUNTRY_CODES = [
  'US', // United States
  'AS', 'GU', 'MP', 'PR', 'VI', // US Territories
  'AF', 'AL', 'DZ', 'AD', 'AO', 'AG', 'AR', 'AM', 'AU', 'AT', 'AZ', 'BS', 'BH', 'BD', 'BB',
  'BY', 'BE', 'BZ', 'BJ', 'BT', 'BO', 'BA', 'BW', 'BR', 'BN', 'BG', 'BF', 'BI', 'CV', 'KH',
  'CM', 'CA', 'CF', 'TD', 'CL', 'CN', 'CO', 'KM', 'CD', 'CG', 'CR', 'HR', 'CU', 'CY', 'CZ',
  'DK', 'DJ', 'DM', 'DO', 'EC', 'EG', 'SV', 'GQ', 'ER', 'EE', 'SZ', 'ET', 'FJ', 'FI', 'FR',
  'GA', 'GM', 'GE', 'DE', 'GH', 'GR', 'GD', 'GT', 'GN', 'GW', 'GY', 'HT', 'HN', 'HU', 'IS',
  'IN', 'ID', 'IR', 'IQ', 'IE', 'IL', 'IT', 'JM', 'JP', 'JO', 'KZ', 'KE', 'KI', 'KW', 'KG',
  'LA', 'LV', 'LB', 'LS', 'LR', 'LY', 'LI', 'LT', 'LU', 'MG', 'MW', 'MY', 'MV', 'ML', 'MT',
  'MH', 'MR', 'MU', 'MX', 'FM', 'MD', 'MC', 'MN', 'ME', 'MA', 'MZ', 'MM', 'NA', 'NR', 'NP',
  'NL', 'NZ', 'NI', 'NE', 'NG', 'KP', 'MK', 'NO', 'OM', 'PK', 'PW', 'PS', 'PA', 'PG', 'PY',
  'PE', 'PH', 'PL', 'PT', 'QA', 'RO', 'RU', 'RW', 'KN', 'LC', 'VC', 'WS', 'SM', 'ST', 'SA',
  'SN', 'RS', 'SC', 'SL', 'SG', 'SK', 'SI', 'SB', 'SO', 'ZA', 'KR', 'SS', 'ES', 'LK', 'SD',
  'SR', 'SE', 'CH', 'SY', 'TW', 'TJ', 'TZ', 'TH', 'TL', 'TG', 'TO', 'TT', 'TN', 'TR', 'TM',
  'TV', 'UG', 'UA', 'AE', 'GB', 'UY', 'UZ', 'VU', 'VA', 'VE', 'VN', 'YE', 'ZM', 'ZW'
];

// US Territories
export const US_TERRITORIES = ['AS', 'GU', 'MP', 'PR', 'VI'];

// US States (2-character ISO-3166 codes)
export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

// Canadian Provinces (2-character ISO-3166 codes)
export const CANADA_PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'ON', 'PE', 'QC', 'SK', 'NT', 'NU', 'YT'];

// Mexican States (3-character ISO-3166 codes)
export const MEXICO_STATES = [
  'AGU', 'BCN', 'BCS', 'CAM', 'CHP', 'CHH', 'COA', 'COL', 'DUR', 'GUA',
  'GRO', 'HID', 'JAL', 'MEX', 'MIC', 'MOR', 'NAY', 'NLE', 'OAX', 'PUE',
  'QUE', 'ROO', 'SLP', 'SIN', 'SON', 'TAB', 'TAM', 'TLA', 'VER', 'YUC',
  'ZAC', 'CMX'
];

export const isUS = (country?: string | null): boolean => (country ?? CountryCode.US) === CountryCode.US;

export const isUSTerritory = (country?: string | null): boolean => {
  if (!country) return false;
  return US_TERRITORIES.includes(country.toUpperCase());
};

export const isCanada = (country?: string | null): boolean => {
  if (!country) return false;
  return country.toUpperCase() === CountryCode.CA;
};

export const isMexico = (country?: string | null): boolean => {
  if (!country) return false;
  return country.toUpperCase() === CountryCode.MX;
};

// Countries that require State/U.S. Territory and ZIP/Postal Code
// US, US Territories (AS, GU, MP, PR, VI), Canada, and Mexico
export const requiresStateAndPostalCode = (country?: string | null): boolean => {
  if (!country) return false;
  const upperCountry = country.toUpperCase();
  return (
    upperCountry === CountryCode.US ||
    US_TERRITORIES.includes(upperCountry) ||
    upperCountry === CountryCode.CA ||
    upperCountry === CountryCode.MX
  );
};

// Validate state code based on country
export const isValidStateForCountry = (state?: string | null, country?: string | null): boolean => {
  if (!state || !country) return false;
  const upperState = state.toUpperCase();
  const upperCountry = country.toUpperCase();

  if (upperCountry === CountryCode.US) {
    return US_STATES.includes(upperState);
  } else if (isUSTerritory(upperCountry)) {
    // For US Territories, state must match the country code
    return upperState === upperCountry;
  } else if (upperCountry === CountryCode.CA) {
    return CANADA_PROVINCES.includes(upperState);
  } else if (upperCountry === CountryCode.MX) {
    return MEXICO_STATES.includes(upperState);
  }
  return false;
};

// Validate postal code format based on country
export const validatePostalCodeFormat = (postalCode?: string | null, country?: string | null): string | null => {
  if (!postalCode) return null;
  
  const trimmedCode = postalCode.trim();
  if (!trimmedCode) return null;

  const upperCountry = country?.toUpperCase();

  // US and US Territories: 5 or 9 numeric digits
  if (upperCountry === CountryCode.US || (upperCountry && US_TERRITORIES.includes(upperCountry))) {
    // Check format: 12345 or 12345-6789
    if (!/^\d{5}(-\d{4})?$/.test(trimmedCode)) {
      return 'Invalid postal code format. US/Territory postal codes must be 5 or 9 numeric digits (e.g., 12345 or 12345-6789)';
    }
    // Check for all zeros
    if (/^0{5}(-0{4})?$/.test(trimmedCode)) {
      return 'Invalid postal code. Postal code cannot be all zeros';
    }
    // Check for 5 digits followed by 4 zeros
    if (/^\d{5}-0{4}$/.test(trimmedCode)) {
      return 'Invalid postal code. Last 4 digits cannot be all zeros';
    }
    return null;
  }

  // Canada: A1A 1A1 format (with or without space)
  if (upperCountry === CountryCode.CA) {
    const canadaPattern = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i;
    if (!canadaPattern.test(trimmedCode)) {
      return 'Invalid Canadian postal code format. Expected format: A1A 1A1';
    }
    return null;
  }

  // Mexico: 5 numeric digits
  if (upperCountry === CountryCode.MX) {
    if (!/^\d{5}$/.test(trimmedCode)) {
      return 'Invalid Mexican postal code format. Expected format: 12345 (5 digits)';
    }
    // Check for all zeros
    if (/^0{5}$/.test(trimmedCode)) {
      return 'Invalid postal code. Postal code cannot be all zeros';
    }
    return null;
  }

  // Other foreign countries: alphanumeric with special characters, max 9 characters
  if (trimmedCode.length > 9) {
    return 'Postal code cannot exceed 9 characters';
  }
  
  // Check for all zeros (any length up to 9)
  if (/^0+$/.test(trimmedCode)) {
    return 'Invalid postal code. Postal code cannot be all zeros';
  }

  return null;
};

// Validate that a country code is a valid ISO-3166-1 alpha-2 code
export const isValidCountryCode = (country?: string | null): boolean => {
  if (!country) return false;
  const upperCountry = country.toUpperCase();
  return VALID_COUNTRY_CODES.includes(upperCountry);
};
